import { useEffect, useRef } from 'react'

const PORTAL_ID = '367443'
const FORM_GUID = 'fce4f53a-6f6e-423d-af9b-7cb1212e26ac'
const FORM_ENDPOINT = `https://forms.hubspot.com/uploads/form/v2/${PORTAL_ID}/${FORM_GUID}`

// Return the public IP of the caller as JSON: { "ip": "x.x.x.x" }.
const IP_LOOKUP_ENDPOINT = 'https://api.ipify.org?format=json'

// localStorage keys holding attribution captured from inbound `?hutk=` /
// `?utm_*=` links by HubSpotAttributionCapture.
export const HUBSPOT_UTK_STORAGE_KEY = 'hs_hutk'
export const HUBSPOT_UTM_STORAGE_KEYS = {
  utm_source: 'hs_utm_source',
  utm_medium: 'hs_utm_medium',
  utm_campaign: 'hs_utm_campaign',
} as const

type Utms = { utm_source: string; utm_medium: string; utm_campaign: string }

// Preferred UTM source: first-party values persisted from the URL, which
// survive the Auth0 round-trip and checkout navigation. Empty when nothing was
// captured, so callers fall back to the Mixpanel cookie.
function readStoredUtms(): Utms {
  const read = (key: string): string => {
    try {
      return localStorage.getItem(key) || ''
    } catch {
      return ''
    }
  }
  return {
    utm_source: read(HUBSPOT_UTM_STORAGE_KEYS.utm_source),
    utm_medium: read(HUBSPOT_UTM_STORAGE_KEYS.utm_medium),
    utm_campaign: read(HUBSPOT_UTM_STORAGE_KEYS.utm_campaign),
  }
}

type MixpanelCookie = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
}

// Fallback for sessions captured into Mixpanel's cookie (e.g. Mixpanel saw the
// URL before HubSpotAttributionCapture persisted it).
function readMixpanelUtms(): Utms {
  const utms = { utm_source: '', utm_medium: '', utm_campaign: '' }
  const match = document.cookie.match(/(?:^|; )mp_[a-f0-9]+_mixpanel=([^;]+)/)
  if (!match) return utms
  try {
    const mp = JSON.parse(decodeURIComponent(match[1])) as MixpanelCookie
    if (mp.utm_source) utms.utm_source = mp.utm_source
    if (mp.utm_medium) utms.utm_medium = mp.utm_medium
    if (mp.utm_campaign) utms.utm_campaign = mp.utm_campaign
  } catch {
    // Malformed cookie — fall through with empty UTMs.
  }
  return utms
}

// Resolves the attribution token. Prefer the hutk captured from an inbound
// `?hutk=` link and persisted to localStorage, it survives navigation and is
// present even though the Portal runs on a separate domain from the HubSpot-
// tracked marketing site, where the hubspotutk cookie isn't set.
function readHubspotUtk(): string | null {
  try {
    const persisted = localStorage.getItem(HUBSPOT_UTK_STORAGE_KEY)
    if (persisted) return persisted
  } catch {
    // localStorage unavailable (private mode / quota) — fall through to cookie.
  }
  const match = document.cookie.match(/(?:^|; )hubspotutk=([^;]+)/)
  return match ? match[1] : null
}

// Submits the membership conversion event to HubSpot once per mount. The
// `hubspotutk` cookie is set by useHubSpotSnippet's loader and ties this
// conversion to the visitor's prior HubSpot-tracked session for attribution.
export function useHubSpotConversion(email: string | undefined) {
  const submitted = useRef(false)

  useEffect(() => {
    if (!email || submitted.current) return
    submitted.current = true

    const stored = readStoredUtms()
    const mixpanel = readMixpanelUtms()
    const utms: Utms = {
      utm_source: stored.utm_source || mixpanel.utm_source,
      utm_medium: stored.utm_medium || mixpanel.utm_medium,
      utm_campaign: stored.utm_campaign || mixpanel.utm_campaign,
    }
    const hutk = readHubspotUtk()

    const submit = (ipAddress: string | null) => {
      const body = new URLSearchParams()
      body.append('email', email)
      body.append('prosci_membership_utm_source', utms.utm_source)
      body.append('prosci_membership_utm_medium', utms.utm_medium)
      body.append('prosci_membership_utm_campaign', utms.utm_campaign)
      body.append(
        'hs_context',
        JSON.stringify({
          hutk,
          ipAddress,
          pageUri: window.location.href,
          pageName: document.title,
        })
      )

      fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      }).catch(() => {
        // Fire-and-forget: tracking failures must not surface to the user.
      })
    }

    // Resolve the IP first, but never block the conversion on it: if the lookup
    // is slow, blocked, or fails, submit without an IP rather than drop the
    // conversion. HubSpot still attributes the contact via email + hutk.
    fetch(IP_LOOKUP_ENDPOINT)
      .then((response) => response.json())
      .then((data: { ip?: string }) => submit(data?.ip ?? null))
      .catch(() => submit(null))
  }, [email])
}
